import { zodResolver } from '@hookform/resolvers/zod'
import { PasswordFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useAuth } from '@web/src/hooks/use-auth'
import { MIN_PASSWORD_LENGTH } from '@web/src/lib/auth/auth-const'
import { useTranslation } from '@web/src/lib/intl/react'
import { TriangleAlertIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { resetPasswordPageHeadFromLoaderData } from './reset-password.seo'

const authFooterLinkClass = cn(buttonVariants({ variant: 'link', size: 'default' }))

export const Route = createFileRoute('/(public)/(main-layout)/auth/reset-password')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
    error: (search.error as string) || '',
  }),
  head: () => resetPasswordPageHeadFromLoaderData(),
})

function ResetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { resetPassword } = useAuth()
  const { token, error: errorToken } = useSearch({ strict: false })

  const formSchema = z
    .object({
      password: z
        .string()
        .min(MIN_PASSWORD_LENGTH, `${t('Password must be at least')} ${MIN_PASSWORD_LENGTH} ${t('characters long')}`),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('Passwords do not match'),
      path: ['confirmPassword'],
    })

  type FormValues = z.infer<typeof formSchema>

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  })

  const passwordRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    passwordRef.current?.focus()
  }, [])

  const onSubmit: SubmitHandler<FormValues> = async ({ password }) => {
    if (!token) {
      return toast.error(t('Reset token is missing'))
    }

    try {
      const result = await resetPassword.mutateAsync({ newPassword: password, token })

      if (result.error) {
        toast.error(result.error.message ?? t('Failed to reset password'))
      } else {
        toast.success(t('Password reset successfully'))

        await navigate({ to: '/auth/login' })
      }
    } catch (err) {
      toast.error((err as Error)?.message ?? t('An error occurred'))
    }
  }

  if (errorToken) {
    return (
      <div className="relative z-10 flex justify-center py-20">
        <Card className="mx-4 w-full max-w-md shadow-2xl">
          <CardHeader className="flex flex-col items-stretch gap-4 text-center">
            <LogoFull size="md" />
            <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row sm:gap-6">
              <TriangleAlertIcon aria-hidden className="size-8 shrink-0 text-destructive sm:size-10" />
              <h1 className="font-semibold text-2xl leading-snug md:text-3xl">{t('Reset Token Invalid')}</h1>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <p className="text-center text-base text-muted-foreground">
              {t(
                'The reset token is no longer valid or expired. Please request a new password reset if you would like to change your password.'
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="relative z-10 flex justify-center py-20">
      <Card className="mx-4 w-full max-w-md shadow-2xl">
        <CardHeader className="flex flex-col items-center gap-2 text-center">
          <LogoFull size="md" />
          <h1 className="mt-2 text-center font-semibold text-lg text-muted-foreground">{t('Reset your password')}</h1>
        </CardHeader>

        <CardContent>
          <Form formMethods={formMethods} onSubmit={onSubmit}>
            <div className="flex flex-col gap-4">
              <PasswordFormField
                autoComplete="new-password"
                label={t('New Password')}
                name="password"
                placeholder={t('Enter your new password')}
                ref={passwordRef}
                required
              />

              <PasswordFormField
                autoComplete="new-password"
                label={t('Confirm New Password')}
                name="confirmPassword"
                placeholder={t('Confirm your new password')}
                required
              />

              <Button className="w-full" disabled={resetPassword.isPending} size="lg" type="submit">
                {resetPassword.isPending ? <Spinner className="mr-2" /> : null}
                {t('Reset Password')}
              </Button>
            </div>
          </Form>
        </CardContent>

        <CardFooter className="justify-center">
          <p className="text-center text-md text-muted-foreground">
            {t('Remember your password?')}{' '}
            <Link className={authFooterLinkClass} to="/auth/login">
              {t('Back to Sign In')}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  )
}
